import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { vrmCheckAction } from 'Context/apiActions';
import { VehicleLookupContext } from 'Context/vehicleLookup';
import { VehicleContext } from 'Context/vehicles';

import { LANDING_PAGE_EVENTS, VALUATIONS_GA_TAGS } from 'Utilities/analytics';
import { HOMEPAGE_GA_TAGS } from 'Utilities/analytics/analyticsHomePage';
import { HERO_ANIMATION_STATES } from 'Utilities/animation';
import { coerceVRM, INTENT, isValidVRM, stripSpaces, unprettyVRM } from 'Utilities/formValidators';
import { useFeatureFlags } from 'Utilities/hooks';
import { findVehicleByVrm } from 'Utilities/vehicles/findBy';
import useFindExistingEnquiry from 'Utilities/vehicles/hooks/useFindVehicleEnquiry';
import { VRM_CHECK_CODES } from 'Utilities/vrm';

import { captureVrmError } from '../vrmInput/VrmInputHelpers';

const checkVRM = (vrmValue: string) => (isValidVRM(vrmValue) ? coerceVRM(vrmValue) : '');

type useVrmInputProps = {
  isHomePageRedesign?: boolean;
  loadingHandler: (value: string) => void;
  onError: (...args: unknown[]) => void;
  onSuccess: (value: string) => void;
  setShowMileage: (value: boolean) => void;
  valuationHub?: boolean;
  vrm: string;
}

const useHeroVrmInput = ({
  isHomePageRedesign = false,
  loadingHandler,
  onError,
  onSuccess,
  setShowMileage,
  valuationHub = false,
  vrm,
}: useVrmInputProps) => {
  const { isRefreshLookupVehicleAfterFetch, isSkippingCreateEnquiryForSellingStates } = useFeatureFlags();
  const history = useHistory();
  const location = useLocation();

  const [vrmValue, setVrmValue] = useState<string>(checkVRM(vrm));
  const { vehicleLookupActions } = useContext(VehicleLookupContext);
  const { vehicleState: vehicles } = useContext(VehicleContext);
  const { getDealershipEnquiry, getRedirectValueForSellingState, isEnquiryInSellingState } = useFindExistingEnquiry();

  useEffect(() => {
    const searchParams = Object.fromEntries(new URLSearchParams(location.search));
    const searchVrm = searchParams?.vrm || '';

    if (isValidVRM(searchVrm)) {
      setVrmValue(coerceVRM(searchVrm));
    } else {
      setVrmValue(checkVRM(vrm));
    }

    return () => setVrmValue(checkVRM(vrm));
  }, [vrm, location]);

  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [wrongVRM, setWrongVRM] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);
  const previousErrors = useRef(errors);

  useEffect(() => {
    if (errors.length && !previousErrors.current.length) {
      const analyticsVector = (isHomePageRedesign) ? HOMEPAGE_GA_TAGS : VALUATIONS_GA_TAGS;
      analyticsVector.HERO_INVALID_VRM_WARNING_SHOWN(vrmValue);
    }
    previousErrors.current = errors;
  }, [errors, isHomePageRedesign, vrmValue]);

  const checkErrors = useCallback(() => {
    const isInvalid = !isValidVRM(vrmValue);

    if (isInvalid) {
      setErrors(['Invalid VRM']);
    }
    return isInvalid;
  }, [vrmValue]);

  const showIntent = useCallback(() => {
    if (errors.length || wrongVRM) {
      return INTENT.ERROR;
    }

    if (isValidVRM(vrmValue)) {
      return INTENT.SUCCESS;
    }

    return null;
  }, [errors.length, wrongVRM, vrmValue]);

  // this is to have the input still show intent without the tooltip when there is an API error
  const showIntentInput = useCallback(() => {
    if (hasApiError && isHomePageRedesign) {
      return null;
    }

    return showIntent();
  }, [hasApiError, isHomePageRedesign, showIntent]);

  const getErrorMsg = useCallback(() => {
    const msg = (errors?.[0] === 'vehicle_lookup_failed')
      ? 'notRegistered'
      : 'errorMessage';

    return msg;
  }, [errors]);

  const checkMileageSkipStep = useCallback(async () => {
    // We only need the mileage step for creating an enquiry,
    // so if an enquiry is in selling state then skip the mileage input
    const currentVehicle = findVehicleByVrm(vehicles, unprettyVRM(vrmValue));
    const dpEnquiry = await getDealershipEnquiry(currentVehicle);
    const isEnquiryInSellingStatus = currentVehicle && isEnquiryInSellingState(currentVehicle, dpEnquiry);
    if (!valuationHub && isEnquiryInSellingStatus && isSkippingCreateEnquiryForSellingStates) {
      vehicleLookupActions.add({ enquiry: currentVehicle?.enquiry, vehicle: currentVehicle });
      const redirectTo = getRedirectValueForSellingState(currentVehicle, dpEnquiry.state.slug);
      VALUATIONS_GA_TAGS.HERO_MILEAGE_SKIPPED();
      if (redirectTo) {
        history.push(redirectTo);
        return true;
      }
    }
    return false;
  }, [
    getDealershipEnquiry,
    getRedirectValueForSellingState,
    history,
    isEnquiryInSellingState,
    isSkippingCreateEnquiryForSellingStates,
    valuationHub,
    vehicleLookupActions,
    vehicles,
    vrmValue,
  ]);

  const onFormSubmit = useCallback(async () => {
    VALUATIONS_GA_TAGS.HERO_VRM_BUTTON_CLICKED_SNOWPLOW(unprettyVRM(vrmValue));

    if (checkErrors()) {
      return;
    }

    if (isHomePageRedesign) {
      HOMEPAGE_GA_TAGS.HERO_VRM_CTA_CLICKED();
    } else {
      VALUATIONS_GA_TAGS.HERO_VRM_BUTTON_CLICKED();
    }

    setLoading(true);
    loadingHandler(HERO_ANIMATION_STATES.vrm);

    if (await checkMileageSkipStep()) {
      return;
    }

    const { error: apiError } = await vrmCheckAction(unprettyVRM(vrmValue), isRefreshLookupVehicleAfterFetch);

    loadingHandler(HERO_ANIMATION_STATES.none);
    if (apiError) {
      captureVrmError(new Error(apiError), unprettyVRM(vrmValue));

      if (([VRM_CHECK_CODES.VEHICLE_LOOKUP_FAILED, VRM_CHECK_CODES.INVALID_VRM, VRM_CHECK_CODES.BAD_REQUEST]
        .includes(apiError))) {
        setHasApiError(true);
        setLoading(false);
        setWrongVRM(true);
      }

      if ([VRM_CHECK_CODES.EXCEEDED].includes(apiError)) {
        setLoading(false);
        setHasApiError(true);
      }

      onError?.(unprettyVRM(vrmValue), apiError, setWrongVRM);
      return;
    }

    VALUATIONS_GA_TAGS.HERO_VEHICLE_LOOKUP_FORM_SUCCESS(unprettyVRM(vrmValue));

    setHasApiError(false);
    setLoading(false);
    setShowMileage(true);
    onSuccess?.(unprettyVRM(vrmValue));
  }, [
    checkErrors,
    checkMileageSkipStep,
    isHomePageRedesign,
    loadingHandler,
    onError,
    onSuccess,
    setShowMileage,
    vrmValue,
    isRefreshLookupVehicleAfterFetch,
  ]);

  const onInputBlur = useCallback(() => {
    if (!vrmValue || checkErrors()) {
      return;
    }
    setVrmValue(coerceVRM(vrmValue));
  }, [checkErrors, vrmValue]);

  const onInputChange = useCallback((value: string) => {
    // Don't prettify VRM onChange because a VRM like `HJ1 ABC`...
    // ...can be coerced into `HJI ABC` when `HJ1 A` is being typed in
    setVrmValue(stripSpaces(value));
    setErrors([]);
  }, []);

  const onInputFocus = useCallback(() => {
    if (isHomePageRedesign) {
      HOMEPAGE_GA_TAGS.HERO_VRM_INPUT_FOCUSSED();
    } else {
      VALUATIONS_GA_TAGS.HERO_VRM_INPUT_FOCUSED();
      LANDING_PAGE_EVENTS.HERO_VRM_INPUT_FOCUSED();
    }
    setErrors([]);
  }, []);

  return {
    getErrorMsg,
    hasApiError,
    loading,
    onFormSubmit,
    onInputBlur,
    onInputChange,
    onInputFocus,
    showIntent,
    showIntentInput,
    vrmValue,
    wrongVRM,
  };
};

export default useHeroVrmInput;
